import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { languages } from '../const/languages';
import { toast } from 'react-toastify';
import { useLanguage } from '../context/LanguageContext';

const LanguageModal = ({ open, onClose, isRequired = false }) => {
  const { language, updateLanguage } = useLanguage();
  const [selectedLanguage, setSelectedLanguage] = useState(language || '');
  
  // Update selected language when the context language changes
  useEffect(() => {
    if (language) {
      setSelectedLanguage(language);
    }
  }, [language]);

  const handleLanguageChange = (event) => {
    setSelectedLanguage(event.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedLanguage) {
      toast.error('Please select a language');
      return;
    }

    try {
      const success = await updateLanguage(selectedLanguage);
      if (success) {
        toast.success('Language preference updated successfully');
        if (onClose) onClose();
      }
    } catch (error) {
      console.error('Language update error:', error);
      toast.error('Failed to update language preference');
    }
  };

  // Modal is only unclosable if it's a required selection (user has no language set and needs to set one)
  const canClose = !isRequired;

  return (
    <Dialog 
      open={open} 
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!canClose}
      onClose={canClose ? onClose : undefined}
      PaperProps={{
        sx: {
          backgroundColor: '#1E1E1E',
          color: 'white',
          borderRadius: '12px',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            Select Your Preferred Language
          </Typography>
          {canClose && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Choose your preferred language for the application interface.
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedLanguage}
              onChange={handleLanguageChange}
              displayEmpty
              sx={{
                backgroundColor: '#2D2D2D',
                color: 'white',
                '& .MuiSelect-icon': {
                  color: 'white',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#404040',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#be58da',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#be58da',
                },
              }}
            >
              <MenuItem value="" disabled>
                <em>— Choose Language —</em>
              </MenuItem>
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                      src={`https://flagcdn.com/24x18/${lang.countryCode.toLowerCase()}.png`}
                      alt={lang.name}
                      style={{ width: 24, height: 18 }}
                    />
                    <span>{lang.name}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button
          onClick={handleSubmit}
          disabled={!selectedLanguage}
          variant="contained"
          fullWidth
          sx={{
            backgroundColor: '#be58da',
            color: 'white',
            '&:hover': {
              backgroundColor: '#a347bf',
            },
            '&.Mui-disabled': {
              backgroundColor: '#4A4A4A',
              color: '#8E8E8E',
            },
          }}
        >
          Save Preference
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LanguageModal; 